import React, { useEffect, useRef, useState } from "react";
import {
  getEntryTypeLabel,
  getEntryTypeRequirements,
  inferGunaNumberInputMode,
  normalizeEntryTypeOptions,
  sanitizeGunaNumber,
} from "../../utils/entryTypeHelpers";

function EntryTypeFields({
  entryTypes,
  selectedEntryType,
  selectedLanguage,
  value,
  errors = {},
  translatedText,
  onEntryTypeChange,
  onFieldChange,
  idPrefix = "entry-type",
  disableEntryTypeSelection = false,
  disableIdentifierInputs = false,
}) {
  const normalizedEntryTypes = normalizeEntryTypeOptions(entryTypes);
  const requirements = getEntryTypeRequirements(
    selectedEntryType,
    normalizedEntryTypes
  );
  const [gunaNumberInputMode, setGunaNumberInputMode] = useState(() =>
    inferGunaNumberInputMode(value?.guna_number)
  );
  const lastSubmittedGunaNumberRef = useRef();
  const getText = (key, fallback) => {
    const translatedValue = translatedText(key);
    return translatedValue === key ? fallback : translatedValue;
  };

  useEffect(() => {
    if (value?.guna_number === lastSubmittedGunaNumberRef.current) {
      return;
    }

    setGunaNumberInputMode(inferGunaNumberInputMode(value?.guna_number));
  }, [value?.guna_number]);

  const handleGunaNumberModeChange = (isFormatted) => {
    setGunaNumberInputMode(isFormatted ? "formatted" : "simple");
  };

  return (
    <>
      <div className="form-group" style={{ gridColumn: "1 / -1" }}>
        <label>
            <span className="text-danger">*</span>
            {getText("entry_type", "Entry Type")}:
        </label>
        <div className="radio-input-option">
          {normalizedEntryTypes.map((option) => {
            const optionId = `${idPrefix}-${option.code}`;

            return (
              <div className="radio-input" key={option.code}>
                <input
                  type="radio"
                  id={optionId}
                  name={`${idPrefix}-group`}
                  value={option.code}
                  checked={selectedEntryType === option.code}
                  disabled={disableEntryTypeSelection}
                  onChange={() => onEntryTypeChange(option.code)}
                />
                <label htmlFor={optionId}>
                  {getEntryTypeLabel(option, selectedLanguage)}
                </label>
              </div>
            );
          })}
        </div>
        {errors.entry_type ? (
          <div className="invalid-feedback" style={{ display: "block" }}>
            {getText("entry_type_validation", "Please select entry type.")}
          </div>
        ) : null}
      </div>

      {requirements.includes("guna_number") ? (
        <div className="form-group">
          <div className="entry-type-guna-header">
            <label
              htmlFor={`${idPrefix}-guna-number`}
              className="entry-type-guna-header__title"
            >
              <span className="text-danger">*</span>
              {translatedText("guna_number")}:
            </label>
          <label
            htmlFor={`${idPrefix}-guna-number-type`}
            className="entry-type-guna-toggle"
            style={{
              cursor: disableIdentifierInputs ? "not-allowed" : "pointer",
            }}
          >
            <input
              type="checkbox"
              id={`${idPrefix}-guna-number-type`}
              className="entry-type-guna-toggle__checkbox"
              checked={gunaNumberInputMode === "formatted"}
              onChange={(event) => handleGunaNumberModeChange(event.target.checked)}
              disabled={disableIdentifierInputs}
            />
            <span className="entry-type-guna-toggle__label">
              {getText("formatted_number", "Formatted Number")}
            </span>
          </label>
          </div>
          <div className="input-group">
            <input
              type="text"
              id={`${idPrefix}-guna-number`}
              className={`form-control ${errors.guna_number ? "is-invalid" : ""}`}
              value={value.guna_number || ""}
              onChange={(event) => {
                const nextValue = sanitizeGunaNumber(
                  event.target.value,
                  gunaNumberInputMode
                );
                lastSubmittedGunaNumberRef.current = nextValue;
                onFieldChange("guna_number", nextValue);
              }}
              placeholder={getText(
                gunaNumberInputMode === "formatted"
                  ? "guna_number_placeholder"
                  : "guna_number_simple_placeholder",
                gunaNumberInputMode === "formatted"
                  ? "Enter FIR number (xxx/yyy/zz)."
                  : "Enter simple number."
              )}
              disabled={disableIdentifierInputs}
            />
          </div>
          {errors.guna_number ? (
            <div className="invalid-feedback">
              {getText("guna_number_validation", "Please enter FIR number.")}
            </div>
          ) : null}
        </div>
      ) : null}

      {requirements.includes("receipt_number") ? (
        <div className="form-group">
          <label htmlFor={`${idPrefix}-receipt-number`}>
            <span className="text-danger">*</span>
            {getText("receipt_number", "Receipt No")}:
          </label>
          <br />
          <div className="input-group">
            <input
              type="text"
              id={`${idPrefix}-receipt-number`}
              className={`form-control ${errors.receipt_number ? "is-invalid" : ""}`}
              value={value.receipt_number || ""}
              onChange={(event) =>
                onFieldChange("receipt_number", event.target.value)
              }
              placeholder={getText(
                "receipt_number_placeholder",
                "Enter receipt number."
              )}
              disabled={disableIdentifierInputs}
            />
          </div>
          {errors.receipt_number ? (
            <div className="invalid-feedback">
              {getText(
                "receipt_number_validation",
                "Please enter receipt number."
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {requirements.includes("other_entry_type") ? (
        <div className="form-group">
          <label htmlFor={`${idPrefix}-other-entry-type`}>
            <span className="text-danger">*</span>
            {getText("other_entry_type", "Other Entry Type")}:
          </label>
          <br />
          <div className="input-group">
            <input
              type="text"
              id={`${idPrefix}-other-entry-type`}
              className={`form-control ${errors.other_entry_type ? "is-invalid" : ""}`}
              value={value.other_entry_type || ""}
              onChange={(event) =>
                onFieldChange("other_entry_type", event.target.value)
              }
              placeholder={getText(
                "other_entry_type_placeholder",
                "Enter other entry type."
              )}
              disabled={disableIdentifierInputs}
            />
          </div>
          {errors.other_entry_type ? (
            <div className="invalid-feedback">
              {getText(
                "other_entry_type_validation",
                "Please enter other entry type."
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default EntryTypeFields;
